'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useUserSafe } from './use-user-safe';
import { logger } from './logger';
import { TourId } from './tours';
import { APIService } from './api-service';
import { isDevMode } from './dev-mode';
import { isHelpSystemEnabled } from './feature-flags';

interface TourState {
  completedTours: Set<TourId>;
  activeTour: TourId | null;
}

interface TourContextValue {
  completedTours: Set<TourId>;
  activeTour: TourId | null;
  startTour: (tourId: TourId) => void;
  completeTour: (tourId: TourId) => void;
  skipTour: (tourId: TourId) => void;
  uncompleteTour: (tourId: TourId) => void;
  resetTour: () => void;
  resetAllTours: () => void;
  hasCompletedTour: (tourId: TourId) => boolean;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

const STORAGE_KEY = 'dt-debtcast-tours';
const apiService = new APIService();

function loadTourStateFromLocalStorage(): TourState {
  if (typeof window === 'undefined') {
    return {
      completedTours: new Set(),
      activeTour: null,
    };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        completedTours: new Set(parsed.completedTours || []),
        activeTour: parsed.activeTour || null,
      };
    }
  } catch (error) {
    logger.warn('Failed to load tour state from localStorage', { error });
  }

  return {
    completedTours: new Set(),
    activeTour: null,
  };
}

function saveTourStateToLocalStorage(state: TourState): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      completedTours: Array.from(state.completedTours),
      activeTour: state.activeTour,
    }));
  } catch (error) {
    logger.warn('Failed to save tour state to localStorage', { error });
  }
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  // Use safe wrapper to avoid API calls in dev mode
  const { user, isLoading: userLoading } = useUserSafe();
  const [state, setState] = useState<TourState>(loadTourStateFromLocalStorage);
  const [isLoading, setIsLoading] = useState(true);
  const [useBackend, setUseBackend] = useState(false);

  // Determine if we should use backend (authenticated user, not dev mode)
  useEffect(() => {
    // Check dev mode first - if in dev mode, never use backend
    // This prevents API calls even if mock user exists in dev mode
    if (isDevMode()) {
      setUseBackend(false);
      return;
    }
    // Only use backend if user is authenticated and not loading
    const shouldUseBackend = !!user && !userLoading;
    setUseBackend(shouldUseBackend);
  }, [user, userLoading]);

  // Load tour state from backend on mount (if authenticated)
  // React 19 Best Practice: Explicit dev mode check to prevent race conditions
  useEffect(() => {
    // Don't load from backend in dev mode - use localStorage only
    if (isDevMode()) {
      setIsLoading(false);
      return;
    }
    
    // Skip backend load if user is not authenticated (prevents 401 errors in console)
    if (!user) {
      setIsLoading(false);
      return;
    }
    
    if (!useBackend || userLoading) {
      setIsLoading(false);
      return;
    }

    const loadFromBackend = async () => {
      // Double-check dev mode before making API call (defense in depth)
      if (isDevMode()) {
        setState(loadTourStateFromLocalStorage());
        setIsLoading(false);
        return;
      }
      
      // Double-check user authentication before making API call
      if (!user) {
        setState(loadTourStateFromLocalStorage());
        setIsLoading(false);
        return;
      }
      
      try {
        // Get access token from server-side API route (Auth0 best practice)
        // Following Auth0 Next.js SDK v4: tokens retrieved server-side, not client-side
        const tokenResponse = await fetch('/api/auth/token', { credentials: 'same-origin' });
        const tokenData = await tokenResponse.json();
        const accessToken = tokenData.accessToken || null;

        // If no access token, skip API call and use localStorage (prevents 401 errors)
        if (!accessToken) {
          setState(loadTourStateFromLocalStorage());
          setIsLoading(false);
          return;
        }

        const backendState = await apiService.getTourCompletion(accessToken);
        setState({
          completedTours: new Set(backendState.completedTours as TourId[]),
          activeTour: (backendState.activeTour as TourId | null) || null,
        });
        logger.info('Loaded tour state from backend', { 
          completedTours: backendState.completedTours.length,
          activeTour: backendState.activeTour 
        });
      } catch (error: unknown) {
        // Handle errors gracefully - fallback to localStorage
        const isUnauthorized = error instanceof Error && error.message.includes('401');
        if (!isUnauthorized) {
          logger.warn('Failed to load tour state from backend, using localStorage', { error });
        }
        // Fallback to localStorage if backend fails
        setState(loadTourStateFromLocalStorage());
      } finally {
        setIsLoading(false);
      }
    };

    loadFromBackend();
  }, [useBackend, userLoading, user]);

  // Save to localStorage as fallback (always)
  useEffect(() => {
    if (!isLoading) {
      saveTourStateToLocalStorage(state);
    }
  }, [state, isLoading]);

  // Sync to backend when state changes (if authenticated)
  // React 19 Best Practice: Explicit dev mode check to prevent race conditions
  useEffect(() => {
    // Don't sync to backend in dev mode - use localStorage only
    // Check dev mode FIRST before any other conditions
    if (isDevMode()) {
      return;
    }
    
    // Skip backend sync if user is not authenticated (prevents 401 errors in console)
    // In development, user might not be authenticated even if not in explicit dev mode
    if (!user) {
      return;
    }
    
    if (!useBackend || isLoading) return;

    const syncToBackend = async () => {
      // Double-check dev mode before making API call (defense in depth)
      if (isDevMode()) {
        return;
      }
      
      // Double-check user authentication before making API call
      if (!user) {
        return;
      }
      
      try {
        // Get access token from server-side API route (Auth0 best practice)
        // Following Auth0 Next.js SDK v4: tokens retrieved server-side, not client-side
        const tokenResponse = await fetch('/api/auth/token', { credentials: 'same-origin' });
        const tokenData = await tokenResponse.json();
        const accessToken = tokenData.accessToken || null;

        // If no access token, skip API call and use localStorage only (prevents 401 errors)
        if (!accessToken) {
          return;
        }

        await apiService.updateTourCompletion({
          completedTours: Array.from(state.completedTours),
          activeTour: state.activeTour,
        }, accessToken);
        // Only log successful syncs in debug mode (not in production)
        logger.debug('Synced tour state to backend', { 
          completedTours: state.completedTours.size,
          activeTour: state.activeTour 
        });
      } catch (error: unknown) {
        // Handle errors gracefully - only log non-401 errors (401 is expected when not authenticated)
        const isUnauthorized = error instanceof Error && error.message.includes('401');
        if (!isUnauthorized) {
          logger.warn('Failed to sync tour state to backend', { error });
        }
        // Continue with localStorage fallback
      }
    };

    // Debounce backend sync to avoid too many requests
    const timeoutId = setTimeout(syncToBackend, 500);
    return () => clearTimeout(timeoutId);
  }, [state, useBackend, isLoading, user]);

  const startTour = useCallback((tourId: TourId) => {
    setState(prev => ({
      ...prev,
      activeTour: tourId,
    }));

    logger.info('Tour started', { tourId });
  }, []);

  const completeTour = useCallback((tourId: TourId) => {
    setState(prev => {
      const newState = {
        ...prev,
        completedTours: new Set([...prev.completedTours, tourId]),
        activeTour: null,
      };
      // Save immediately to prevent race conditions with TourManager
      saveTourStateToLocalStorage(newState);
      return newState;
    });

    logger.info('Tour completed', { tourId });
  }, []);

  const skipTour = useCallback((tourId: TourId) => {
    setState(prev => ({
      ...prev,
      activeTour: null,
    }));

    logger.info('Tour skipped', { tourId });
  }, []);

  const uncompleteTour = useCallback((tourId: TourId) => {
    setState(prev => {
      const newCompletedTours = new Set(prev.completedTours);
      newCompletedTours.delete(tourId);
      return {
        ...prev,
        completedTours: newCompletedTours,
        activeTour: null,
      };
    });

    logger.info('Tour uncompleted', { tourId });
  }, []);

  const resetTour = useCallback(() => {
    setState(prev => ({
      ...prev,
      activeTour: null,
    }));
  }, []);

  const resetAllTours = useCallback(() => {
    setState({
      completedTours: new Set(),
      activeTour: null,
    });
    logger.info('All tours reset');
  }, []);

  const hasCompletedTour = useCallback((tourId: TourId) => {
    return state.completedTours.has(tourId);
  }, [state.completedTours]);

  const value: TourContextValue = {
    completedTours: state.completedTours,
    activeTour: state.activeTour,
    startTour,
    completeTour,
    skipTour,
    uncompleteTour,
    resetTour,
    resetAllTours,
    hasCompletedTour,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

// Default values when TourProvider is not available (help system disabled)
const defaultTourContextValue: TourContextValue = {
  completedTours: new Set(),
  activeTour: null,
  startTour: () => {},
  completeTour: () => {},
  skipTour: () => {},
  uncompleteTour: () => {},
  resetTour: () => {},
  resetAllTours: () => {},
  hasCompletedTour: () => false,
};

export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  
  // If help system is disabled, return default values instead of throwing
  if (context === undefined) {
    if (!isHelpSystemEnabled()) {
      return defaultTourContextValue;
    }
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
